import React, { FC, useMemo, useState } from 'react';
import { Customer } from '@/types';
import { CustomColumns, CustomTable, faEdit, Icon } from '@/ui/components';
import {
  useCustomers,
  useUpdateCustomer,
  useCreateCustomer,
  useCustomersHierarchy,
} from '@/api/Customers';
import { Button, ColorPicker, Switch } from 'antd';
import { useTranslate } from '@/translations';
import { useAppCustomers } from '@/hooks';
import { CustomerPopup } from '../CustomerPopup';
import { useAppDataRules, useAppFeatures } from '@/api/AppFeatures';

export const CustomersDataGrid: FC = () => {
  const { t } = useTranslate();
  const { hasGbaAdminAccess } = useAppCustomers();

  const {
    data: customersData,
    isFetching: isCustomersLoading,
    refetch: reloadCustomers,
  } = useCustomers();

  const {
    data: customersHierarchy,
    isFetching: isCustomersHierarchyLoading,
    refetch: reloadCustomersHierarchy,
  } = useCustomersHierarchy();

  const { data: allAppFeatures, isFetching: isFeaturesLoading } =
    useAppFeatures();

  const { data: allAppDataRules, isFetching: isDataRulesLoading } =
    useAppDataRules();

  const { mutateAsync: updateMutate, isPending: isUpdateLoading } =
    useUpdateCustomer();
  const { mutateAsync: createMutate, isPending: isCreateLoading } =
    useCreateCustomer();

  const isLoading =
    isCustomersLoading ||
    isCustomersHierarchyLoading ||
    isFeaturesLoading ||
    isDataRulesLoading ||
    isUpdateLoading ||
    isCreateLoading;

  const [customer, setCustomer] = useState<Customer>();

  const onSubmit = async (data: Customer) => {
    const res = await (data.customerId
      ? updateMutate(data)
      : createMutate(data));

    if (res.success) {
      await Promise.all([reloadCustomers(), reloadCustomersHierarchy()]);
    }

    return res.message?.Message;
  };

  const columns: CustomColumns<Customer> = useMemo(
    () => [
      {
        dataIndex: 'customerCode',
        title: 'Code',
        sortable: true,
        filter: true,
      },
      {
        dataIndex: 'customerName',
        title: 'Name',
        sortable: true,
        filter: true,
      },
      {
        dataIndex: 'iconUrl',
        title: 'Icon',
        width: 150,
        align: 'center',
        render: (value) => (
          <img src={value} alt="customer icon" style={{ height: '35px' }} />
        ),
      },
      {
        dataIndex: 'color1',
        title: 'Foreground Colour',
        align: 'center',
        width: 150,
        render: (value) => <ColorPicker defaultValue={value} disabled />,
      },
      {
        dataIndex: 'color2',
        title: 'Background Colour',
        align: 'center',
        width: 150,
        render: (value) => <ColorPicker defaultValue={value} disabled />,
      },
      {
        dataIndex: 'isActive',
        title: 'Active',
        width: 80,
        align: 'center',
        render: (value) => <Switch checked={value} />,
      },
      {
        dataIndex: 'customerId',
        render: (_, data) =>
          hasGbaAdminAccess ? (
            <Button onClick={() => setCustomer({ ...data })}>
              <Icon icon={faEdit} /> {t('edit')}
            </Button>
          ) : null,
        width: 150,
      },
    ],
    [hasGbaAdminAccess, t],
  );

  return (
    <>
      <CustomTable
        dataSource={customersData || []}
        columns={columns}
        rowKey="customerId"
        loading={isLoading}
        onAdd={() => setCustomer({} as Customer)}
      />

      <CustomerPopup
        data={customer}
        hierarchyData={customersHierarchy}
        appFeatures={allAppFeatures}
        appDataRules={allAppDataRules}
        onCancel={() => setCustomer(undefined)}
        onSubmit={onSubmit}
      />
    </>
  );
};
